/**
 * Ensures the browser's URL query string accurately reflects the current language
 * state determined by the backend. This runs once on page load to correct any
 * discrepancies (e.g., language loaded from a cookie but not present in the URL).
 */
export const syncLanguageToUrl = (): void => {
  const langInfo = window.commonVariables?.language;

  // If language management is not active or data is missing, do nothing.
  if (!langInfo) {
    return;
  }

  const { current: currentLanguage, default: defaultLanguage } = langInfo;
  const url = new URL(window.location.href);
  const langParam = url.searchParams.get("lang");

  let needsUrlUpdate = false;

  // Case 1: The page is in a non-default language, but the URL doesn't show it.
  // This happens when navigating to a new page where the language is determined by a cookie.
  if (currentLanguage !== defaultLanguage && langParam !== currentLanguage) {
    url.searchParams.set("lang", currentLanguage);
    needsUrlUpdate = true;
  }

  // Case 2: The page is in the default language, but the URL has an unnecessary `lang` parameter.
  // This cleans up the URL for the default language, which is good for SEO and clarity.
  if (currentLanguage === defaultLanguage && langParam) {
    url.searchParams.delete("lang");
    needsUrlUpdate = true;
  }

  // If an update is needed, use replaceState to change the URL without a page reload
  // and without adding a new entry to the browser's history.
  if (needsUrlUpdate) {
    window.history.replaceState({}, "", url.toString());
    console.log(
      `[Language Sync] URL updated to reflect current language: ${currentLanguage}`
    );
  }
};
